import { type FormEvent, useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { QUESTIONS } from "../config";
import type { AdminDashboardPayload } from "../types";

const CSRF_STORAGE_KEY = "kitty-admin-csrf";

const formatDate = (timestamp: number) => timestamp > 0
  ? new Date(timestamp).toLocaleString("cs-CZ", { dateStyle: "medium", timeStyle: "short" })
  : "Bez data";

const formatAnswer = (questionId: string, answer: unknown) => {
  if (questionId === "love" && typeof answer === "number") return `${answer} %`;
  const value = Array.isArray(answer) && answer.every((item) => typeof item === "string")
    ? answer.join(" · ")
    : typeof answer === "string"
    ? answer.trim()
    : typeof answer === "number" || typeof answer === "boolean"
      ? String(answer)
      : "";
  return value || "Bez odpovědi";
};

export function Admin({ onExit }: { onExit: () => void }) {
  const [pin, setPin] = useState("");
  const [loggedIn, setLoggedIn] = useState(false);
  const [dashboard, setDashboard] = useState<AdminDashboardPayload>();
  const [message, setMessage] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState<string>();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [csrfToken, setCsrfToken] = useState(() => sessionStorage.getItem(CSRF_STORAGE_KEY) ?? "");

  const respondents = useMemo(() => {
    const answers = dashboard?.answers ?? [];
    const replies = dashboard?.finalReplies ?? (dashboard?.finalReply ? [dashboard.finalReply] : []);
    const sessionIds = new Set([...answers.map((answer) => answer.sessionId), ...replies.map((reply) => reply.sessionId)]);

    return [...sessionIds].map((sessionId) => {
      const sessionAnswers = answers.filter((answer) => answer.sessionId === sessionId);
      const finalReply = replies.find((reply) => reply.sessionId === sessionId);
      const identity = sessionAnswers.find((answer) => answer.questionId === "identity");
      const timestamps = [
        ...sessionAnswers.map((answer) => answer.answeredAt ? Date.parse(answer.answeredAt) : 0),
        finalReply ? Date.parse(finalReply.createdAt) : 0,
      ];

      return {
        sessionId,
        name: identity ? String(identity.answer) : "Neznámá osoba",
        answers: sessionAnswers,
        finalReply,
        lastActivity: Math.max(...timestamps, 0),
      };
    }).sort((a, b) => b.lastActivity - a.lastActivity);
  }, [dashboard]);

  const selectedRespondent = respondents.find((respondent) => respondent.sessionId === selectedSessionId);
  const openDetail = (sessionId: string) => {
    setSelectedSessionId(sessionId);
    setConfirmDelete(false);
    setDeleteError("");
  };
  const closeDetail = () => {
    setSelectedSessionId(undefined);
    setConfirmDelete(false);
    setDeleteError("");
  };

  const load = async () => {
    try {
      const data = await api.adminDashboard();
      setDashboard(data);
      setMessage(data.finalMessage ?? "");
      setLoggedIn(true);
    } catch {
      setLoggedIn(false);
    }
  };

  useEffect(() => {
    void Promise.resolve().then(load);
  }, []);

  useEffect(() => {
    if (!selectedRespondent) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setSelectedSessionId(undefined);
        setConfirmDelete(false);
        setDeleteError("");
      }
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [selectedRespondent]);

  const login = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setNotice("");
    try {
      const result = await api.adminLogin(pin);
      sessionStorage.setItem(CSRF_STORAGE_KEY, result.csrfToken);
      setCsrfToken(result.csrfToken);
      await load();
    } catch {
      setNotice("PIN nesedí nebo je teď potřeba chvíli počkat.");
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (!csrfToken) {
      setLoggedIn(false);
      return;
    }
    setBusy(true);
    setNotice("");
    try {
      await api.saveFinalMessage(message, csrfToken);
      setNotice("Zpráva je uložená.");
    } catch {
      setNotice("Uložení se nepovedlo. Zkus se znovu přihlásit.");
    } finally {
      setBusy(false);
    }
  };

  const refresh = async () => {
    setBusy(true);
    setNotice("");
    await load();
    setBusy(false);
  };

  const logout = async () => {
    if (csrfToken) await api.adminLogout(csrfToken).catch(() => undefined);
    sessionStorage.removeItem(CSRF_STORAGE_KEY);
    onExit();
  };

  const deleteRespondent = async () => {
    if (!selectedRespondent) return;
    if (!csrfToken) {
      setLoggedIn(false);
      return;
    }
    setDeleting(true);
    setDeleteError("");
    try {
      await api.deleteRespondent(selectedRespondent.sessionId, csrfToken);
      closeDetail();
      await load();
    } catch {
      setDeleteError("Odstranění se nepovedlo. Obnov přehled a zkus to znovu.");
    } finally {
      setDeleting(false);
    }
  };

  if (!loggedIn) return <LoginForm pin={pin} busy={busy} notice={notice} onExit={onExit} onPin={setPin} onLogin={(event) => { void login(event); }} />;

  return <main className="admin">
    <header className="admin__header">
      <div><p className="admin__eyebrow">SOUKROMÁ ČÁST</p><h1>Odpovědi</h1></div>
      <div className="admin__actions">
        <button className="admin__button admin__button--ghost" onClick={() => { void refresh(); }} disabled={busy}>{busy ? "NAČÍTÁM…" : "OBNOVIT"}</button>
        <button className="admin__button admin__button--ghost" onClick={() => { void logout(); }}>Odhlásit</button>
      </div>
    </header>

    <section className="admin__layout">
      <article className="admin-card admin-card--people">
        <div className="admin-card__heading">
          <div><p className="admin__eyebrow">LIDÉ</p><h2>Vyber jméno</h2></div>
          <span className="admin-card__count">{respondents.length}</span>
        </div>
        {respondents.length === 0 && <p className="admin__empty">Zatím nikdo neodpověděl.</p>}
        <div className="people-list">
          {respondents.map((respondent) => {
            const answeredCount = respondent.answers.filter((answer) => answer.questionId !== "identity").length;
            return <button className="person-row" key={respondent.sessionId} onClick={() => openDetail(respondent.sessionId)}>
              <span className="person-row__avatar" aria-hidden="true">{respondent.name.trim().charAt(0).toLocaleUpperCase("cs-CZ") || "?"}</span>
              <span className="person-row__main"><strong>{respondent.name}</strong><small>{answeredCount} z {QUESTIONS.length - 1} odpovědí · {formatDate(respondent.lastActivity)}</small></span>
              {respondent.finalReply && <span className="person-row__badge">Zpráva</span>}
              <span className="person-row__arrow" aria-hidden="true">›</span>
            </button>;
          })}
        </div>
      </article>

      <article className="admin-card admin-card--message">
        <p className="admin__eyebrow">ZÁVĚR</p><h2>Moje finální zpráva</h2>
        <p className="admin-card__description">Tento text uvidí každý po dokončení všech otázek.</p>
        <textarea rows={7} value={message} onChange={(event) => setMessage(event.target.value)} placeholder="Napiš svoji finální zprávu…" />
        <button className="admin__button" onClick={() => { void save(); }} disabled={busy}>{busy ? "UKLÁDÁM…" : "ULOŽIT ZPRÁVU"}</button>
        {notice && <p className="admin__notice" role="status">{notice}</p>}
      </article>
    </section>

    {selectedRespondent && <div className="admin-detail-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) closeDetail(); }}>
      <section className="admin-detail" role="dialog" aria-modal="true" aria-labelledby="admin-detail-title">
        <header className="admin-detail__header">
          <div><p className="admin__eyebrow">ODPOVĚDI</p><h2 id="admin-detail-title">{selectedRespondent.name}</h2><p>{formatDate(selectedRespondent.lastActivity)}</p></div>
          <button className="admin-detail__close" onClick={closeDetail} aria-label="Zavřít detail" autoFocus>×</button>
        </header>

        <div className="admin-detail__summary">
          <span>{selectedRespondent.answers.filter((answer) => answer.questionId !== "identity").length} / {QUESTIONS.length - 1} zodpovězeno</span>
          <span>{selectedRespondent.finalReply ? "Finální zpráva odeslána" : "Bez finální zprávy"}</span>
        </div>

        <div className="answer-list">
          {QUESTIONS.filter((question) => question.id !== "identity").map((question, index) => {
            const answer = selectedRespondent.answers.find((item) => item.questionId === question.id);
            return <article className={`answer-item ${answer ? "" : "answer-item--empty"}`} key={question.id}>
              <span className="answer-item__number">{String(index + 1).padStart(2, "0")}</span>
              <div><h3>{question.title}</h3><p>{answer ? formatAnswer(question.id, answer.answer) : "Zatím bez odpovědi"}</p>{answer?.answeredAt && <time>{new Date(answer.answeredAt).toLocaleString("cs-CZ")}</time>}</div>
            </article>;
          })}
        </div>

        <article className="admin-detail__reply">
          <p className="admin__eyebrow">FINÁLNÍ ZPRÁVA</p>
          <p>{selectedRespondent.finalReply?.message || "Zatím nic neposlala."}</p>
        </article>

        <div className="admin-detail__danger">
          {!confirmDelete
            ? <button className="admin__button admin__button--danger-ghost" onClick={() => setConfirmDelete(true)}>Odstranit osobu</button>
            : <div className="admin-delete-confirm" role="alert">
              <div><strong>Opravdu odstranit {selectedRespondent.name}?</strong><p>Trvale se smažou všechny její odpovědi i finální zpráva.</p></div>
              <div className="admin-delete-confirm__actions">
                <button className="admin__button admin__button--ghost" onClick={() => { setConfirmDelete(false); setDeleteError(""); }} disabled={deleting}>ZRUŠIT</button>
                <button className="admin__button admin__button--danger" onClick={() => { void deleteRespondent(); }} disabled={deleting}>{deleting ? "ODSTRAŇUJI…" : "ANO, ODSTRANIT"}</button>
              </div>
              {deleteError && <p className="admin__notice" role="alert">{deleteError}</p>}
            </div>}
        </div>
      </section>
    </div>}
  </main>;
}

function LoginForm({ pin, busy, notice, onExit, onPin, onLogin }: { pin: string; busy: boolean; notice: string; onExit: () => void; onPin: (pin: string) => void; onLogin: (event: FormEvent) => void }) {
  return <main className="admin admin-login">
    <button className="admin__back" onClick={onExit}>← Zpět</button>
    <form onSubmit={onLogin} className="admin-login__card">
      <p className="admin__eyebrow">SOUKROMÁ ČÁST</p><h1>Admin</h1><label htmlFor="pin">Šestimístný PIN</label>
      <input id="pin" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} value={pin} onChange={(event) => onPin(event.target.value.replace(/\D/g, ""))} required />
      <button className="admin__button" disabled={pin.length !== 6 || busy}>{busy ? "OVĚŘUJI…" : "ODEMKNOUT"}</button>
      {notice && <p role="alert" className="admin__notice">{notice}</p>}
    </form>
  </main>;
}
